import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {disablesUsers, enablesUsers} from "../../../../api/user/UserAccountService";
import BtnSearchDetail from "../../../../components/buttons/BtnSearchDetail";
import SearchDetailDrawer from "../../../../components/search/SearchDetailDrawer";
import SearchSection from "../../../../components/search/SearchSection";
import Selector from "../../../../components/selector/Selector";
import {searchListener, searchWithChildren} from "../../../../components/commonmethod/simmsDrawerMethod";
import {extractObject} from "../../../../components/commonmethod/CommonMethod";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import DetailLinkedPagination from "../../../../components/detailLayout/DetailLinkedPagination";
import {handleActiveError} from "../../../../components/sonner/ToastBasic";
import JsGridExcelGridOpenButton from "../../../../components/js-grid/JsGridExcelGridOpenButton";
import UserAccountManageSearchBar from "./UserAccountManageSearchBar";
import UserAccountManageSidebarList from "./UserAccountManageSidebarList";
import UserAccountManageDetailPanel from "./UserAccountManageDetailPanel";
import UserAccountCreateForm from "./UserAccountCreateForm";
import "../../../../components/detailLayout/detailLinkedList.css";
import "../../company/companyManagePanel.css";
import "../userAccountManagePanel.css";

function UserAccountEditorEmpty({loading, hasSelection}) {
    if (loading) {
        return (
            <div className="code-editor-empty">
                <div className="code-editor-empty__icon">…</div>
                <p className="code-editor-empty__title">불러오는 중</p>
                <p className="code-editor-empty__desc">사용자 정보를 가져오고 있습니다.</p>
            </div>
        );
    }

    return (
        <div className="code-editor-empty">
            <div className="code-editor-empty__icon">⌘</div>
            <p className="code-editor-empty__title">
                {hasSelection ? "사용자를 선택하세요" : "등록된 사용자가 없습니다"}
            </p>
            <p className="code-editor-empty__desc">
                {hasSelection
                    ? "왼쪽 목록에서 사용자를 선택하면 이곳에서 상세 정보를 확인할 수 있습니다."
                    : "검색 조건을 변경하거나 새 사용자를 등록해 주세요."}
            </p>
        </div>
    );
}

const UserAccountListType = forwardRef(function UserAccountListType(
    {data = [], dataHook, pageInfo, listType, viewMode = "list", onViewModeChange},
    ref,
) {
    const {handleSearch, saveConditions, refresh, handleGridPageChange} = dataHook;

    const [searchParams] = useSearchParams();
    const createFormRef = useRef(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [checkedIds, setCheckedIds] = useState([]);
    const detailButtonRef = useRef(null);
    const selectedIdRef = useRef(null);

    const excelGridSearch = useMemo(() => extractObject(saveConditions), [saveConditions]);
    const gridPageable = useMemo(() => ({
        pageNumber: pageInfo?.currentPage ?? 0,
        totalPages: Math.max(1, pageInfo?.totalPages ?? 1),
        totalElements: pageInfo?.totalElements ?? 0,
        pageSize: pageInfo?.size ?? 20,
        size: pageInfo?.size ?? 20,
    }), [pageInfo]);

    useEffect(() => {
        setCheckedIds([]);
        setSelectedId(null);
        selectedIdRef.current = null;
    }, [listType]);

    useEffect(() => {
        const idParam = searchParams.get("selectedId");
        if (!idParam) {
            return;
        }

        const parsedId = Number(idParam);
        if (!Number.isFinite(parsedId)) {
            return;
        }

        selectedIdRef.current = parsedId;
        setSelectedId(parsedId);
    }, [searchParams]);

    useEffect(() => {
        setCheckedIds([]);
    }, [data]);

    const handleSelectUser = useCallback((item) => {
        if (item?.id == null || item.id === selectedIdRef.current) {
            return;
        }
        selectedIdRef.current = item.id;
        setSelectedId(item.id);
    }, []);

    const handleToggleCheck = useCallback((id) => {
        setCheckedIds((prev) => (
            prev.includes(id) ? prev.filter((each) => each !== id) : [...prev, id]
        ));
    }, []);

    const handleToggleAll = useCallback((checked) => {
        if (!checked) {
            setCheckedIds([]);
            return;
        }
        setCheckedIds(data.map((item) => item.id).filter((id) => id != null));
    }, [data]);

    const toggleAccountState = useCallback(async () => {
        const targetIds = checkedIds.length > 0
            ? checkedIds
            : (selectedIdRef.current != null ? [selectedIdRef.current] : []);

        if (!targetIds.length) {
            alert("대상을 선택하세요");
            return;
        }

        const confirmMessage = listType
            ? "선택한 사용자를 비활성화 하시겠습니까?"
            : "선택한 사용자를 활성화 하시겠습니까?";
        if (!window.confirm(confirmMessage)) {
            return;
        }

        const request = listType ? disablesUsers : enablesUsers;
        const response = await request({checked: targetIds});
        if (response?.status === 200) {
            alert(listType ? "비활성화 되었습니다." : "활성화 되었습니다.");
            selectedIdRef.current = null;
            setSelectedId(null);
            setCheckedIds([]);
            refresh();
            return;
        }
        if (response?.status === 201 || response?.status === 202) {
            handleActiveError({message: response.data.title, description: response.data.reason});
        }
    }, [checkedIds, listType, refresh]);

    const handleCreateSuccess = useCallback((newUserId) => {
        onViewModeChange?.("list");
        refresh();
        if (newUserId != null) {
            selectedIdRef.current = newUserId;
            setSelectedId(newUserId);
        }
    }, [onViewModeChange, refresh]);

    const handleEditSuccess = useCallback(() => {
        refresh();
    }, [refresh]);

    useImperativeHandle(ref, () => ({
        toggleAccountState,
        submitCreate: () => createFormRef.current?.submit(),
    }), [toggleAccountState]);

    const filterSection = (
        <SearchSection>
            <Selector
                showLabel
                auth
                onChange={(value) => searchListener(handleSearch, "company", extractObject(saveConditions), value)}
                data={saveConditions.company}
                entity="company"
                title="업체"
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
            <Selector
                showLabel
                auth
                onChange={(value) => searchWithChildren(handleSearch, "department", extractObject(saveConditions), value)}
                data={saveConditions.department}
                recursive
                entity="department"
                title="부서"
            />
        </SearchSection>
    );

    const showEditor = selectedId != null;
    const isCreateMode = viewMode === "create";

    if (isCreateMode) {
        return (
            <div className="code-manage-panel__body company-manage-panel user-account-manage-panel">
                <UserAccountCreateForm
                    ref={createFormRef}
                    embedded
                    hideActions
                    onCancel={() => onViewModeChange?.("list")}
                    onSuccess={handleCreateSuccess}
                />
            </div>
        );
    }

    return (
        <div className="code-manage-panel__body company-manage-panel user-account-manage-panel">
            <div className="company-manage-panel__topbar code-manage-scope">
                <UserAccountManageSearchBar
                    saveConditions={saveConditions}
                    handleSearch={handleSearch}
                    detailButton={(
                        <BtnSearchDetail
                            isDetailOpen={isDetailOpen}
                            setIsDetailOpen={setIsDetailOpen}
                            detailButtonRef={detailButtonRef}
                        />
                    )}
                />
                <div className="company-manage-panel__actions">
                    <JsGridExcelGridOpenButton entityName="UserAccount" search={excelGridSearch} />
                </div>
            </div>

            <SearchDetailDrawer open={isDetailOpen} onClose={() => setIsDetailOpen(false)}>
                {filterSection}
            </SearchDetailDrawer>

            <div className="company-manage-panel__workspace">
                <div className="code-manage-panel__list code-manage-scope company-manage-panel__list user-account-manage-panel__list">
                    <UserAccountManageSidebarList
                        items={data}
                        selectedId={selectedId}
                        checkedIds={checkedIds}
                        onSelect={handleSelectUser}
                        onToggleCheck={handleToggleCheck}
                        onToggleAll={handleToggleAll}
                    />
                    <div className="user-account-manage-panel__list-footer js-grid-pagination">
                        <DetailLinkedPagination
                            pageable={gridPageable}
                            onPageChange={handleGridPageChange}
                        />
                    </div>
                </div>

                <div className="code-manage-panel__editor code-manage-scope company-manage-panel__editor user-account-manage-panel__editor">
                    {!showEditor ? (
                        <UserAccountEditorEmpty
                            loading={dataHook.loading}
                            hasSelection={!!data?.length}
                        />
                    ) : (
                        <UserAccountManageDetailPanel
                            key={selectedId}
                            userId={selectedId}
                            onEditSuccess={handleEditSuccess}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

export default UserAccountListType;
